import { Product } from "~/domains/product/Product";
import { Box, Button, CardContent, Stack, styled } from "~/common/mui";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { type ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PriceData } from "~/domains/pricing/types/PriceData";
import { useTranslation } from "react-i18next";
import { isNonNull } from "~/common/utils/reducers";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { getCountriesList } from "~/domains/product/utils/productDescriptorUtils";
import { getTariffNumbers } from "~/utils/productUtils";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { Parameter, ParameterStack } from "~/common/components/ParameterV2";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import Delete from "~/common/mui/icons/Delete";

interface CartEntrySummaryProps {
  product: ProductDescriptor;
  totalQuantity: number;
  lineItemCount: number;
  subtotal: PriceData | null;
  hasTariffSurcharge: boolean;
  hasIncludedSurcharge: boolean;
  canDeleteProduct: boolean;
  editMode: boolean;
  onDeleteProduct: () => void;
}

export const CartEntrySummary = ({
  product,
  totalQuantity,
  lineItemCount,
  subtotal,
  hasTariffSurcharge,
  hasIncludedSurcharge,
  canDeleteProduct,
  editMode,
  onDeleteProduct,
}: CartEntrySummaryProps) => {
  const { t } = useTranslation();

  const params = [
    ProductParams2.SchaefflerId({ product, t }),
    {
      label: "Tariff number",
      value: getTariffNumbers(product.base).join(", "),
    },
    {
      label: "Country of origin",
      value: getCountriesList({ product: product.base }).join(", "),
    },
  ].filter((p): p is { label: string; value: string } => isNonNull(p) && !!p.value);

  const isOnSale = useSalesHelper(product).isProductOnSale();

  return (
    <ProductDescriptorContext value={product}>
      <CardSummary sx={{ position: "relative" }}>
        <Box sx={{ gridArea: "image" }}>
          <Product.PdpLink>
            <Product.PrimaryImageWithLogosV2 size={120} logoWidth={32} gap={16} />
          </Product.PdpLink>
          {isOnSale && <FloatingSaleTag size={32} />}
        </Box>

        <Stack gap={2} sx={{ gridArea: "summary" }}>
          <Stack gap={0.5}>
            <Product.DesignationContainer>
              <Product.PreferredIndicator />
              <Product.PdpLink>
                <Product.DesignationV2 variant="titleMedium" fontWeight={"bold"} />
              </Product.PdpLink>
            </Product.DesignationContainer>
            <Product.MMDTag />
            <Product.ShortDescriptionV2 />
          </Stack>

          <ParameterStack parameterVariant={"row"}>
            {params.map((data) => (
              <Parameter key={data.label} {...data} />
            ))}
          </ParameterStack>
          <Product.ReconditionableTag />
        </Stack>

        <Box sx={{ gridArea: "quantity" }}>
          <Parameter variant="column" label={t("cartPage.table.header.quantity.title")} value={totalQuantity} />
        </Box>

        <Box sx={{ gridArea: "lines" }}>
          <Parameter variant="column" label={t("cartPage.summary.productLines")} value={lineItemCount} />
        </Box>

        <Box sx={{ gridArea: "subtotal", justifySelf: "flex-end", textAlign: "end" }}>
          <Parameter
            variant="column"
            label={t("cartPage.table.subtotal")}
            slotProps={{
              value: {
                sx: {
                  fontWeight: "bold",
                },
              },
            }}
            value={
              <Price price={subtotal} surchargeEnabled tariffSurcharge={hasTariffSurcharge || hasIncludedSurcharge} />
            }
          />
        </Box>

        {NewVersionMessage.isVisible(product.base) && (
          <Box sx={{ gridArea: "new-version", justifySelf: "flex-start" }}>
            <NewVersionMessage baseProduct={product.base} />
          </Box>
        )}

        {editMode && (
          <Box sx={{ gridArea: "delete-product", justifySelf: "flex-end" }}>
            <Button
              variant="text"
              startIcon={<Delete />}
              color="textPrimary"
              onClick={onDeleteProduct}
              disabled={!canDeleteProduct}
            >
              {t("cartPage.button.deleteProduct")}
            </Button>
          </Box>
        )}
      </CardSummary>
    </ProductDescriptorContext>
  );
};

const CardSummary = styled(CardContent)(({ theme: _theme }) => ({
  padding: "24px",
  display: "grid",
  columnGap: "32px",
  rowGap: "24px",
  gridTemplateAreas: `"image summary     quantity    lines          subtotal"
                      "image new-version new-version delete-product delete-product"`,
  gridTemplateColumns: "168px 10fr 3fr 3fr 4fr",
}));

const FloatingSaleTag = styled(SaleTag)({
  position: "absolute",
  top: "0",
  left: "0",
});
